import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import {
  actOnRequest, getBackstopAssignees, getBackstopRequest, getBackstopRequests, getRequestBackstopSummary,
} from '../services/requestBackstopApi'
import type { BackstopAction } from '../services/requestBackstopApi'
import { useEtlJobCatalog } from './useEtlJobs'
import { deriveWriteGate } from '../utils/adminGate'
import type { AdminGate } from '../utils/adminGate'
import { errorKind, errorStatus } from '../utils/format'
import { SLOW, pollUnlessRefused } from '../utils/polling'

/**
 * The request backstop's reads, and its interventions (Cartographer doc 15 §7).
 *
 * Every key sits under `['observatory', 'requests']`, and every intervention invalidates that prefix, so the list, the
 * summary, the rail badge and the drawer re-read what the server decided — this module never claims a request left the
 * backstop.
 *
 * SLOW, and stopped by a refusal (pollUnlessRefused): a request reaches the backstop on the hourly sweep or when a person
 * acts in Cartographer, so faster polling would re-read an unchanged page. Every read is LighthouseAdmin, so each takes
 * the gate contract's `enabled` (hooks/useLighthouseAdmin.ts): the page passes `adminPageMayFetch(gate)`, the badge
 * `gate.state === 'admin'`.
 *
 * None of them retries a 403 (the role will not appear on a second asking) or a 503 (the request table is not
 * provisioned, which no retry fixes).
 */

/** The prefix every backstop query sits under, and so what every intervention invalidates. */
export const REQUEST_BACKSTOP_PREFIX = ['observatory', 'requests'] as const

/** Retry anything but a refusal or a missing table, twice at most. */
export function shouldRetryBackstop(failureCount: number, error: unknown): boolean {
  const kind = errorKind(error)
  return kind !== 'forbidden' && kind !== 'not-found' && errorStatus(error) !== 503 && failureCount < 2
}

/** The page header's and the rail badge's one shared read. */
export function useRequestBackstopSummary(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...REQUEST_BACKSTOP_PREFIX, 'summary'],
    queryFn: getRequestBackstopSummary,
    enabled: options?.enabled ?? true,
    refetchInterval: pollUnlessRefused(SLOW),
    retry: shouldRetryBackstop,
  })
}

/** One page of the backstop. The query is the key by value, so a page is never served another page's rows. */
export function useBackstopRequests(query: { skip: number; top: number }, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...REQUEST_BACKSTOP_PREFIX, 'list', query],
    queryFn: () => getBackstopRequests(query),
    enabled: options?.enabled ?? true,
    refetchInterval: pollUnlessRefused(SLOW),
    retry: shouldRetryBackstop,
  })
}

/** One request, for the drawer — asked only while an id is set and the gate allows it. A 404 is an answer. */
export function useBackstopRequest(id: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...REQUEST_BACKSTOP_PREFIX, 'detail', id],
    queryFn: () => getBackstopRequest(id!),
    enabled: id != null && id !== '' && (options?.enabled ?? true),
    refetchInterval: pollUnlessRefused(SLOW),
    retry: shouldRetryBackstop,
  })
}

/** Who a commission may be reassigned to — asked only while the reassign form is open. Not polled. */
export function useBackstopAssignees(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...REQUEST_BACKSTOP_PREFIX, 'assignees', id],
    queryFn: () => getBackstopAssignees(id),
    enabled,
    staleTime: 60_000,
    retry: shouldRetryBackstop,
  })
}

/**
 * Whether this session may intervene: the catalogue's server-evaluated `canHandleRequests` (deriveWriteGate). Controls
 * render ONLY on `admin`; an absent flag is `denied` (this API has no backstop), never `unknown`.
 */
export function useRequestHandlingGate(): AdminGate {
  const { data, isError } = useEtlJobCatalog()
  return deriveWriteGate(data, isError, 'canHandleRequests')
}

/** Whether a failed intervention leaves the cached request behind the server: a 409 says it changed since it was read. */
export function interventionFailureInvalidates(error: unknown): boolean {
  return errorKind(error) === 'conflict'
}

/** Re-reads every backstop query — an intervention can change the list, the summary's counts and the drawer. */
export function invalidateBackstop(queryClient: Pick<QueryClient, 'invalidateQueries'>): void {
  void queryClient.invalidateQueries({ queryKey: [...REQUEST_BACKSTOP_PREFIX] })
}

/**
 * One intervention (bearer-only). On success — and on a 409, which means the cache is behind — it invalidates the
 * `['observatory', 'requests']` prefix and says nothing itself: the caller words the toast from the response.
 */
export function useActOnRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: BackstopAction) => actOnRequest(request),
    onSuccess: () => invalidateBackstop(queryClient),
    onError: (error: unknown) => {
      if (interventionFailureInvalidates(error)) invalidateBackstop(queryClient)
    },
  })
}
